import { motion } from 'framer-motion';

/**
 * Floating reaction emojis scattered over a discover card's blurred background.
 * Each distinct reaction is drawn once, sized by how many users picked it
 * (more of the same reaction → bigger). Purely decorative (pointer-events off).
 */

// Scatter anchors spread across the whole card background. The track card sits
// on top (higher z-index, opaque artwork), so any anchors landing behind it are
// naturally occluded — keeping the song's center crisp while the reactions live
// purely in the blurred background.
const ANCHORS = [
  { top: '6%', left: '10%' },
  { top: '12%', left: '80%' },
  { top: '24%', left: '4%' },
  { top: '30%', left: '88%' },
  { top: '4%', left: '46%' },
  { top: '38%', left: '16%' },
  { top: '42%', left: '72%' },
  { top: '18%', left: '92%' },
  { top: '52%', left: '8%' },
  { top: '10%', left: '30%' },
  { top: '58%', left: '84%' },
  { top: '48%', left: '40%' },
  { top: '64%', left: '22%' },
  { top: '2%', left: '66%' },
] as const;

export default function FloatingReactions({ entries }: { entries: [string, number][] }) {
  if (entries.length === 0) return null;

  const maxCount = Math.max(...entries.map(([, n]) => n));

  // Declutter: the more distinct reactions a card has, the smaller everything
  // is drawn, so a busy card doesn't overwhelm the artwork.
  const densityScale = Math.max(0.6, 1 - (entries.length - 1) * 0.05);
  const minSize = 20 * densityScale;
  const maxSize = 58 * densityScale;

  return (
    <div aria-hidden="true" className="tw:pointer-events-none tw:absolute tw:inset-0 tw:z-0">
      {entries.map(([emoji, count], i) => {
        const anchor = ANCHORS[i % ANCHORS.length];
        // Size scales with relative popularity. The >1 exponent pushes
        // low-count emojis smaller, so the most-reacted one clearly stands out;
        // the whole range is shrunk by densityScale when there are many.
        const ratio = maxCount > 0 ? count / maxCount : 0;
        const size = Math.round(minSize + Math.pow(ratio, 1.6) * (maxSize - minSize));
        return (
          <motion.span
            key={emoji}
            className="tw:absolute tw:select-none"
            style={{
              top: anchor.top,
              left: anchor.left,
              fontSize: size,
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))',
            }}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 0.9, scale: 1, y: [0, -7, 0] }}
            transition={{
              opacity: { duration: 0.4 },
              scale: { type: 'spring', stiffness: 260, damping: 18 },
              y: { duration: 3 + (i % 4) * 0.6, repeat: Infinity, ease: 'easeInOut' },
            }}
          >
            {emoji}
          </motion.span>
        );
      })}
    </div>
  );
}
